import React, {useContext, useEffect, useState} from 'react';
import {ElementType, FormType, FormTypeContext} from '../types';
import IconLock from '../../icon/IconLock';
import {MenuItem} from '../../../ui/ActionMenu';
import {useHistory} from 'react-router';
import DeleteFieldDlg from './DeleteFieldDlg';
import {ReactSortable} from 'react-sortablejs';
import InteractiveIcon from '../InteractiveIcon';
import IconListMove from '../../icon/IconListMove';
import {Link} from 'react-router-dom';
import SectionConfiguration from '../../../models/form/SectionConfiguration';
import {LanguageId} from '../../../models/utils/Languages';
import FieldConfiguration from '../../../models/form/FieldConfiguration';
import DeleteSectionDlg from './DeleteSectionDlg';
import NonInteractiveIcon from '../NonInteractiveIcon';
import Tip from '../../../ui/Tip';
import {createUseStyles} from 'react-jss';
import cn from 'classnames';
import Tooltip from '../../../ui/Tooltip';
import IconLeft from '../../icon/IconLeft';
import IconRight from '../../icon/IconRight';
import ActionToolbar from '../../../ui/ActionToolbar';

type Props = {
  url: string
  language: LanguageId
  selectedSectionIds: string[]
  sections: SectionConfiguration[]
  createOption?: boolean
  onDelete: (type: ElementType, id: string) => void
}

interface ScrollPosition {
  top: boolean
  bottom: boolean
}

export default function SideFormListOfFields({
  url, onDelete, selectedSectionIds, sections, language, createOption = false,
}: Props) {
  const [showDeleteFieldDialog, setShowDeleteFieldDialog] = useState(false);
  const [showDeleteSectionDialog, setShowDeleteSectionDialog] = useState(false);
  const [selectedField, setSelectedField] = useState<FieldConfiguration | undefined>(undefined);
  const [selectedSection, setSelectedSection] = useState<SectionConfiguration | undefined>(undefined);
  const [position, setPosition] = useState(0);
  const [scrollPosition, setScrollPosition] = useState<ScrollPosition>({top: true, bottom: false});
  const [lastEl, setLastEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setLastEl(document.getElementById('last-element'))
  }, []);

  const sorted = sections.sort((a, b) => a.getLabel(language).localeCompare(b.getLabel(language)))

  const history = useHistory();
  const formTypeContext = useContext(FormTypeContext)

  const handleEditField = (e: React.MouseEvent, field: FieldConfiguration) => {
    e.stopPropagation();
    history.push(`${url}/field/${field.id}`);
  }

  const handleDeleteField = (e: React.MouseEvent, field: FieldConfiguration) => {
    e.stopPropagation();
    setSelectedField(field);
    setShowDeleteFieldDialog(true);
  }

  const fieldActionItems = (field: FieldConfiguration): MenuItem[] => {
    return [
      {title: 'Edit', iconId: 'edit', onClick: (e: React.MouseEvent) => handleEditField(e, field)},
      {title: 'Delete', iconId: 'delete', onClick: (e: React.MouseEvent) => handleDeleteField(e, field)},
    ]
  };

  const handleEditSection = (e: React.MouseEvent, section: SectionConfiguration) => {
    e.stopPropagation();
    history.push(`${url}/section/${section.id}`);
  }

  const handleDeleteSection = (e: React.MouseEvent, section: SectionConfiguration) => {
    e.stopPropagation();
    setSelectedSection(section);
    setShowDeleteSectionDialog(true);
  }

  const sectionActionItems = (section: SectionConfiguration): MenuItem[] => {
    return [
      {title: 'Edit', iconId: 'edit', onClick: (e: React.MouseEvent) => handleEditSection(e, section)},
      {title: 'Delete', iconId: 'delete', onClick: (e: React.MouseEvent) => handleDeleteSection(e, section)},
    ]
  };


  const lockedSection = (section: SectionConfiguration) => section.id === 'custom' || selectedSectionIds.indexOf(section.id) >= 0;
  const reason = (section: SectionConfiguration) => {
    if (section.id === 'custom') {
      return 'This is a system section. It cannot be added'
    } else if (selectedSectionIds.indexOf(section.id) >= 0) {
      return 'This section is already added'
    }
    return 'You cannot add custom sections to this form';
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleOrderChange = (reorderedFields: FieldConfiguration[]) => false

  const styles = createUseStyles({
    scrollable: {
      overflowY: "scroll",
      scrollbarWidth: "none",
      '-ms-overflow-style': "none",
      '&::-webkit-scrollbar': {
        width: '0px'
      },
      height: '75vh',
    },
    fields: {
      '&:hover ~ span > div': {
        backgroundColor: '#F3F8FF'
      }
    }
  })();

  const sidebarEl = document.getElementById('side-bar');

  const scrollBottom = () => {
    const lastElementPosition = lastEl?.offsetTop || 600 - 500;
    const newPosition = position + 500 > lastElementPosition ? position : position + 500;
    setScrollPosition({top: newPosition === 0, bottom: newPosition === position});
    sidebarEl?.scrollTo({top: newPosition, behavior: 'smooth'});
    setPosition(newPosition);
  }

  const scrollTop = () => {
    const newPosition = position - 500 > 0 ? position - 500 : 0;
    setScrollPosition({top: newPosition === 0, bottom: newPosition === position});
    sidebarEl?.scrollTo({top: newPosition, behavior: 'smooth'});
    setPosition(newPosition);
  }

  return (
    <div className="flex flex-col items-center">
      {selectedField &&
      <DeleteFieldDlg id={selectedField?.id} name={selectedField?.getLabel(language)} show={showDeleteFieldDialog}
        setShowDialog={setShowDeleteFieldDialog} onDelete={onDelete}/>
      }
      {selectedSection &&
      <DeleteSectionDlg id={selectedSection?.id} name={selectedSection?.getLabel(language)}
        show={showDeleteSectionDialog}
        setShowDialog={setShowDeleteSectionDialog} onDelete={onDelete}/>
      }

      <span
        onClick={() => scrollTop()}
        className={cn('rounded-3xl border border-gray w-10 h-10 bg-white -mt-8 cursor-pointer', {'hidden': scrollPosition.top})}>
        <IconLeft className="ml-2.5 mt-4 transform rotate-90"/></span>
      <div id="side-bar" className={cn('relative -mx-4', styles.scrollable)}>
        <h2 className="font-bold text-2xl pb-2 flex justify-between mx-4">
          <span>Sections & {formTypeContext.capitalizeToken()}s</span>
        </h2>
        {createOption &&
        <div className="flex mb-2 mt-2 ml-4">
          <Link className="text-blue mr-10" to={`${url}/section`}>Create section</Link>
          <Link className="text-blue" to={`${url}/field`}>Create {formTypeContext.token}</Link>
        </div>
        }
        {!createOption && formTypeContext.type === FormType.EvaluationForm &&
        <Tip className="mt-1 -mb-1">You can add only system sections and questions to this form</Tip>
        }
        <div className={cn('my-2 flex flex-col mx-2')}>
          <div id="first-element"/>
          <ReactSortable list={sorted.map((x) => x.toSkeleton())} setList={(state) => state}
            group="sections" sort={false} filter='.filtered'
            ghostClass="block-sortable-ghost" dragClass="block-sortable-drag"
            animation={250} easing="cubic-bezier(0.45, 0, 0.55, 1)">
            {sorted.map((section) => (
              <div key={`side-${section.id}`} id={`side-${section.id}`}
                className={lockedSection(section) ? 'filtered' : ''}>
                <>
                  <div
                    className={cn('p-3 mt-4 -mx-2 -my-2 grid grid-cols-12 hover:bg-blue-section rounded-lg', styles.fields)}>
                    <div className="col-span-10">
                      {!lockedSection(section) &&
                      <InteractiveIcon><IconListMove className="text-gray-dark "/></InteractiveIcon>
                      }
                      {lockedSection(section) &&
                      <NonInteractiveIcon>
                        <Tooltip content={reason(section)}
                          label={`${section.id}.tip`} className="pl-0.5">
                          <IconLock className="ml-0.5 text-gray-dark "/>
                        </Tooltip>
                      </NonInteractiveIcon>
                      }
                      <span className="font-bold text-xl">{section.getLabel(language)}</span>
                    </div>
                    <div className="col-span-2">
                      {section.editable &&
                      <ActionToolbar items={sectionActionItems(section)}/>
                      }
                    </div>
                  </div>
                  {section.fields.length > 0 &&
                  <ReactSortable list={section.fields} setList={(newState) => handleOrderChange(newState)}
                    group={{name: "shared", pull: true, put: false}} animation={200} direction="vertical" sort={false}
                    ghostClass="block-sortable-ghost" dragClass="block-sortable-drag"
                    tag="span"
                    forceFallback={true}>
                    {section.fields.map((field) => (
                      <div
                        className="grid grid-cols-12 cursor-pointer group pl-6 p-3 -my-2 -mx-2 rounded-lg hover:bg-blue-section "
                        onClick={() => false}
                        key={field.id}>
                      <span className="col-span-10">
                          <InteractiveIcon><IconListMove className="text-gray-dark "/></InteractiveIcon>
                        {field.getLabel(language)}
                      </span>
                        <span className="col-span-2">
                        {field.editable && createOption &&
                        <ActionToolbar items={fieldActionItems(field)}/>
                        }
                        </span>
                      </div>
                    ))}
                  </ReactSortable>
                  }
                </>
              </div>
            ))}
          </ReactSortable>
        </div>
        <div id="last-element"/>
      </div>
      <span onClick={() => scrollBottom()}
        className={cn('rounded-3xl border border-gray w-10 h-10 fixed bottom-8 bg-white cursor-pointer', {'hidden': scrollPosition.bottom})}><IconRight
        className="ml-2.5 mt-4 transform rotate-90"/></span>
    </div>
  );
}
